// ── what a page lost and gained, line by line (the fold sheet) ──────────────
// A plain LCS over lines, bounded (a page past MAX lines is shown whole as
// replaced, never diffed slowly), with long runs of unchanged lines folded to a
// few lines of context either side. Pure and client-safe.

export type DiffLine = { t: 'same' | 'add' | 'del'; line: string } | { t: 'gap'; n: number }

const MAX = 1200

export function lineDiff(before: string, after: string, context = 2): DiffLine[] {
  const a = before.split('\n')
  const b = after.split('\n')
  if (a.length > MAX || b.length > MAX) {
    return [...a.map((line) => ({ t: 'del' as const, line })), ...b.map((line) => ({ t: 'add' as const, line }))]
  }
  // lcs table, from the end
  const m = a.length
  const n = b.length
  const dp: Uint16Array[] = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1))
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) dp[i][j] = a[i] === b[j] ? dp[i + 1][j + 1] + 1 : Math.max(dp[i + 1][j], dp[i][j + 1])
  const raw: Array<{ t: 'same' | 'add' | 'del'; line: string }> = []
  let i = 0
  let j = 0
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      raw.push({ t: 'same', line: a[i] })
      i++
      j++
    } else if (dp[i + 1][j] >= dp[i][j + 1]) raw.push({ t: 'del', line: a[i++] })
    else raw.push({ t: 'add', line: b[j++] })
  }
  while (i < m) raw.push({ t: 'del', line: a[i++] })
  while (j < n) raw.push({ t: 'add', line: b[j++] })
  // fold the long unchanged runs
  const keep = raw.map((r, k) => r.t !== 'same' || raw.slice(Math.max(0, k - context), k + context + 1).some((x) => x.t !== 'same'))
  const out: DiffLine[] = []
  let gap = 0
  raw.forEach((r, k) => {
    if (keep[k]) {
      if (gap) out.push({ t: 'gap', n: gap })
      gap = 0
      out.push(r)
    } else gap++
  })
  if (gap) out.push({ t: 'gap', n: gap })
  return out
}

/** how many lines a diff adds and takes away */
export function diffCounts(d: ReadonlyArray<DiffLine>): { added: number; removed: number } {
  let added = 0
  let removed = 0
  for (const x of d) {
    if (x.t === 'add') added++
    if (x.t === 'del') removed++
  }
  return { added, removed }
}
